import { nanoid } from 'nanoid';
import { ACTION_TYPE, ELEMENT_TYPE, type EventName } from '@/constants';
import type {
  ActionData,
  BookData,
  BookPageData,
  ElementAttrs,
  ElementConfig,
  ElementMetaData,
  ProjectData,
  TextStyleData
} from '@/types';

export function createProjectData(name: string = '新建工程'): ProjectData {
  return {
    id: `book-${nanoid(6)}`,
    name,
    version: '1.0.0'
  };
}

export function createBookData(title: string, width: number, height: number): BookData {
  return {
    id: `book-${nanoid(6)}`,
    title: title,
    width,
    height,
    pages: []
  };
}

export function createPageData(): BookPageData {
  return {
    id: `page-${nanoid(6)}`,
    actions: []
  };
}

export function createImageActionData(url: string, attrs?: ElementAttrs): ActionData {
  const id = `image-${nanoid(6)}`;

  return {
    id,
    type: ACTION_TYPE.MEDIA,
    startTime: 0,
    duration: 2,
    element: {
      id,
      type: ELEMENT_TYPE.IMAGE,
      relativePath: url,
      content: url,
      attrs
    }
  };
}

/** 尾页本地图：content 存 Vite 根相对路径，加载时由 resolveProjectPaths 按静态基路径解析 */
export function createTailPageImageActionData(
  url: string,
  role: NonNullable<ElementConfig['tailPageRole']>,
  attrs?: ElementAttrs,
  config?: ElementConfig
): ActionData {
  const id = `tail-page-${nanoid(6)}`;

  return {
    id,
    type: ACTION_TYPE.MEDIA,
    startTime: 0,
    duration: 2,
    element: {
      id,
      type: ELEMENT_TYPE.IMAGE,
      relativePath: url,
      content: url,
      attrs,
      config: {
        ...config,
        tailPageRole: role
      }
    }
  };
}

export function createEventActionData(
  eventName: EventName,
  attrs?: ElementAttrs,
  config?: Omit<ElementConfig, 'emitterEventName'>
): ActionData {
  const id = `event-${nanoid(6)}`;

  return {
    id,
    type: ACTION_TYPE.EVENT,
    startTime: 0,
    duration: 2,
    element: {
      id,
      type: ELEMENT_TYPE.EVENT,
      attrs,
      config: {
        ...config,
        emitterEventName: eventName
      }
    }
  };
}

export function createTextActionData(
  content: string,
  style?: TextStyleData,
  attrs?: ElementAttrs
): ActionData {
  const id = `text-${nanoid(6)}`;

  if (!style) {
    style = createDefaultTextStyleData();
  }

  return {
    id,
    type: ACTION_TYPE.TEXT,
    startTime: 0,
    duration: 2,
    element: {
      id,
      type: ELEMENT_TYPE.TEXT,
      content: {
        text: content,
        style
      },
      attrs
    }
  };
}

/**
 * 创建 音频ActionData
 * @param content 音频资源 URL
 * @param name    音轨名称
 * @param config  多媒体配置
 */
export function createSoundActionData(
  content: string,
  name?: string,
  config?: ElementConfig,
  meta?: ElementMetaData
): ActionData {
  const id = `sound-${nanoid(6)}`;
  return {
    id,
    type: ACTION_TYPE.SOUND,
    startTime: 0,
    duration: 2,
    element: {
      id,
      type: ELEMENT_TYPE.SOUND,
      relativePath: content,
      content,
      name,
      config,
      meta
    }
  };
}

export function createDefaultTextStyleData(style?: TextStyleData, isBlack = true): TextStyleData {
  const textColor = isBlack ? '#000000' : '#ffffff';
  const strokeColor = isBlack ? '#ffffff' : '#000000';

  const defaultStyle: TextStyleData = {
    fill: {
      fill: textColor
    },
    font: {
      fontFamily: 'Arial',
      fontSize: 30
    },
    stroke: {
      color: strokeColor,
      width: 2
    }
  };

  if (!isBlack) {
    defaultStyle.shadow = {
      color: '#000000',
      alpha: 0.3,
      distance: 2,
      angle: (80 / 180) * Math.PI,
      blur: 2
    };
  }

  if (style) {
    return { ...defaultStyle, ...style };
  }

  return defaultStyle;
}
